import React, { useState, useEffect } from 'react';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import './Dashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

function Dashboard() {
    const [loading, setLoading] = useState(true);
    const [kpis, setKpis] = useState(null);
    const [teacherDistribution, setTeacherDistribution] = useState([]);
    const [weeklyTrend, setWeeklyTrend] = useState([]);
    const [topPunctual, setTopPunctual] = useState([]);
    const [lowAttendance, setLowAttendance] = useState([]);
    const [machineStatus, setMachineStatus] = useState([]);
    const [recentPunches, setRecentPunches] = useState([]);
    const [attendanceByDay, setAttendanceByDay] = useState([]);
    const [weeklyAttendance, setWeeklyAttendance] = useState([]);
    const [monthlyAttendance, setMonthlyAttendance] = useState([]);
    const [monthlyYears, setMonthlyYears] = useState({ current: new Date().getFullYear(), last: new Date().getFullYear() - 1 });

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const [
                kpisRes,
                distributionRes,
                trendRes,
                punctualRes,
                lowAttendRes,
                machineRes,
                punchesRes,
                byDayRes,
                weeklyAttendRes,
                monthlyAttendRes
            ] = await Promise.all([
                fetch(`${API_BASE}/dashboard/kpis`),
                fetch(`${API_BASE}/dashboard/teacher-distribution`),
                fetch(`${API_BASE}/dashboard/weekly-trend`),
                fetch(`${API_BASE}/dashboard/top-punctual`),
                fetch(`${API_BASE}/dashboard/low-attendance`),
                fetch(`${API_BASE}/dashboard/machine-status`),
                fetch(`${API_BASE}/dashboard/recent-punches`),
                fetch(`${API_BASE}/dashboard/attendance-by-day`),
                fetch(`${API_BASE}/dashboard/weekly-attendance`),
                fetch(`${API_BASE}/dashboard/monthly-attendance`)
            ]);

            const kpisData = await kpisRes.json();
            const distributionData = await distributionRes.json();
            const trendData = await trendRes.json();
            const punctualData = await punctualRes.json();
            const lowAttendData = await lowAttendRes.json();
            const machineData = await machineRes.json();
            const punchesData = await punchesRes.json();
            const byDayData = await byDayRes.json();
            const weeklyAttendData = await weeklyAttendRes.json();
            const monthlyAttendData = await monthlyAttendRes.json();

            if (kpisData.success) setKpis(kpisData.data);
            if (distributionData.success) setTeacherDistribution(distributionData.data);
            if (trendData.success) setWeeklyTrend(trendData.data);
            if (punctualData.success) setTopPunctual(punctualData.data);
            if (lowAttendData.success) setLowAttendance(lowAttendData.data);
            if (machineData.success) setMachineStatus(machineData.data);
            if (punchesData.success) setRecentPunches(punchesData.data);
            if (byDayData.success) setAttendanceByDay(byDayData.data);
            if (weeklyAttendData.success) setWeeklyAttendance(weeklyAttendData.data);
            if (monthlyAttendData.success) {
                setMonthlyAttendance(monthlyAttendData.data);
                if (monthlyAttendData.currentYear) setMonthlyYears({ current: monthlyAttendData.currentYear, last: monthlyAttendData.lastYear });
            }

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const pieChartData = {
        labels: teacherDistribution.map(d => d.label),
        datasets: [{
            data: teacherDistribution.map(d => d.value),
            backgroundColor: ['#2266E3', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'],
            hoverBackgroundColor: ['#1D4ED8', '#16A34A', '#D97706', '#DC2626', '#7C3AED', '#DB2777']
        }]
    };

    const weeklyChartData = {
        labels: weeklyTrend.map(d => d.day?.substring(0, 3) || d.date),
        datasets: [{
            label: 'Teachers Present',
            data: weeklyTrend.map(d => d.attended),
            fill: true,
            backgroundColor: 'rgba(34, 102, 227, 0.2)',
            borderColor: '#2266E3',
            tension: 0.4
        }]
    };

    const byDayChartData = {
        labels: attendanceByDay.map(d => d.day?.substring(0, 3)),
        datasets: [{
            label: 'Avg Attendance',
            data: attendanceByDay.map(d => d.avgAttendance),
            backgroundColor: '#22C55E'
        }]
    };

    const chartOptions = {
        plugins: {
            legend: { position: 'bottom' }
        },
        maintainAspectRatio: false
    };

    const barChartOptions = {
        plugins: {
            legend: { display: false }
        },
        scales: {
            y: { beginAtZero: true }
        },
        maintainAspectRatio: false
    };

    const currentYear = new Date().getFullYear();

    const weeklyAttendChartData = {
        labels: weeklyAttendance.map(d => d.week_label),
        datasets: [
            {
                label: `${currentYear}`,
                data: weeklyAttendance.map(d => d.thisYear),
                backgroundColor: '#2266E3'
            },
            {
                label: `${currentYear - 1}`,
                data: weeklyAttendance.map(d => d.lastYear),
                backgroundColor: '#CBD5E1'
            }
        ]
    };

    const monthlyAttendChartData = {
        labels: monthlyAttendance.map(d => d.month_label),
        datasets: [
            {
                label: `${monthlyYears.last}`,
                data: monthlyAttendance.map(d => d.lastYear),
                backgroundColor: '#CBD5E1'
            },
            {
                label: `${monthlyYears.current}`,
                data: monthlyAttendance.map(d => d.thisYear),
                backgroundColor: '#22C55E'
            }
        ]
    };

    const comparisonChartOptions = {
        plugins: {
            legend: { position: 'bottom' }
        },
        scales: {
            y: { beginAtZero: true, title: { display: true, text: 'Avg Teachers/Day' } }
        },
        maintainAspectRatio: false
    };

    const formatDateTime = (dateTime) => {
        if (!dateTime) return '-';
        const date = new Date(dateTime);
        return date.toLocaleString('en-US', { 
            month: 'short', day: 'numeric', 
            hour: '2-digit', minute: '2-digit' 
        });
    };

    const machineStatusTemplate = (rowData) => {
        return <Tag severity="success" value="Active" />;
    };

    const punctualityTemplate = (rowData) => {
        const value = rowData.punctuality || 0;
        const severity = value >= 80 ? 'success' : value >= 50 ? 'warning' : 'danger';
        return (
            <div style={{ width: '100%' }}>
                <ProgressBar value={value} showValue={true} 
                    style={{ height: '20px' }}
                    color={severity === 'success' ? '#22C55E' : severity === 'warning' ? '#F59E0B' : '#EF4444'} />
            </div>
        );
    };

    const attendanceRateTemplate = (rowData) => {
        const value = rowData.attendanceRate || 0;
        return <Tag severity={value < 30 ? 'danger' : 'warning'} value={`${value}%`} />;
    };

    if (loading) {
        return (
            <div className="dashboard-container">
                <div className="dashboard-kpis">
                    {[1,2,3,4,5,6].map(i => (
                        <Card key={i} className="kpi-card">
                            <Skeleton width="60%" height="1.5rem" className="mb-2" />
                            <Skeleton width="40%" height="2.5rem" />
                        </Card>
                    ))}
                </div>
                <div className="dashboard-charts">
                    <Card className="chart-card"><Skeleton height="300px" /></Card>
                    <Card className="chart-card"><Skeleton height="300px" /></Card>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h2>Ananda College - Attendance Dashboard</h2>
                <p>Real-time attendance monitoring and analytics</p>
            </div>

            {/* KPI Cards */}
            <div className="dashboard-kpis">
                <Card className="kpi-card" style={{ cursor: 'help' }} pt={{ root: { title: 'Total number of active teachers registered in the system' } }}>
                    <div className="kpi-icon" style={{ backgroundColor: '#EEF2FF' }}>
                        <i className="pi pi-users" style={{ color: '#2266E3' }}></i>
                    </div>
                    <div className="kpi-content">
                        <span className="kpi-label">Total Teachers</span>
                        <span className="kpi-value">{kpis?.totalTeachers || 0}</span>
                    </div>
                </Card>

                <Card className="kpi-card" style={{ cursor: 'help' }} pt={{ root: { title: 'Teachers who have registered their fingerprint on a machine' } }}>
                    <div className="kpi-icon" style={{ backgroundColor: '#F0FDF4' }}>
                        <i className="pi pi-id-card" style={{ color: '#22C55E' }}></i>
                    </div>
                    <div className="kpi-content">
                        <span className="kpi-label">Enrolled</span>
                        <span className="kpi-value">{kpis?.enrolledTeachers || 0}</span>
                    </div>
                </Card>

                <Card className="kpi-card" style={{ cursor: 'help' }} pt={{ root: { title: 'Number of teachers who have punched in today' } }}>
                    <div className="kpi-icon" style={{ backgroundColor: '#FEF3C7' }}>
                        <i className="pi pi-check-circle" style={{ color: '#F59E0B' }}></i>
                    </div>
                    <div className="kpi-content">
                        <span className="kpi-label">Today Present</span>
                        <span className="kpi-value">{kpis?.todayAttended || 0}</span>
                    </div>
                </Card>

                <Card className="kpi-card" style={{ cursor: 'help' }} pt={{ root: { title: 'Average on-time arrival score for this month (weekdays only). 100% = all teachers arrived by 7:20 AM' } }}>
                    <div className="kpi-icon" style={{ backgroundColor: '#FEE2E2' }}>
                        <i className="pi pi-clock" style={{ color: '#EF4444' }}></i>
                    </div>
                    <div className="kpi-content">
                        <span className="kpi-label">Avg Punctuality</span>
                        <span className="kpi-value">{kpis?.avgPunctuality || 0}%</span>
                    </div>
                </Card>

                <Card className="kpi-card" style={{ cursor: 'help' }} pt={{ root: { title: 'Average daily attendance score for this month (weekdays only). Based on arrival time — earlier arrival earns a higher score' } }}>
                    <div className="kpi-icon" style={{ backgroundColor: '#F3E8FF' }}>
                        <i className="pi pi-chart-line" style={{ color: '#8B5CF6' }}></i>
                    </div>
                    <div className="kpi-content">
                        <span className="kpi-label">Avg Daily Score</span>
                        <span className="kpi-value">{kpis?.avgDailyScore || 0}%</span>
                    </div>
                </Card>

                <Card className="kpi-card" style={{ cursor: 'help' }} pt={{ root: { title: 'Number of fingerprint machines currently registered in the system' } }}>
                    <div className="kpi-icon" style={{ backgroundColor: '#FCE7F3' }}>
                        <i className="pi pi-server" style={{ color: '#EC4899' }}></i>
                    </div>
                    <div className="kpi-content">
                        <span className="kpi-label">Active Machines</span>
                        <span className="kpi-value">{kpis?.activeMachines || 0}</span>
                    </div>
                </Card>
            </div>

            {/* Charts Row */}
            <div className="dashboard-charts">
                <Card className="chart-card" title="Weekly Attendance Trend">
                    <div className="chart-container">
                        <Chart type="line" data={weeklyChartData} options={chartOptions} />
                    </div>
                </Card>

                <Card className="chart-card" title="Teacher Distribution">
                    <div className="chart-container">
                        <Chart type="doughnut" data={pieChartData} options={chartOptions} />
                    </div>
                </Card>

                <Card className="chart-card" title="Attendance by Day of Week">
                    <div className="chart-container">
                        <Chart type="bar" data={byDayChartData} options={barChartOptions} />
                    </div>
                </Card>
            </div>

            {/* Comparison Charts Row */}
            <div className="dashboard-charts" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <Card className="chart-card" title="Weekly Attendance (Last 5 Weeks)">
                    <div className="chart-container">
                        <Chart type="bar" data={weeklyAttendChartData} options={comparisonChartOptions} />
                    </div>
                </Card>

                <Card className="chart-card" title="Monthly Attendance (Jan - Dec)">
                    <div className="chart-container">
                        <Chart type="bar" data={monthlyAttendChartData} options={comparisonChartOptions} />
                    </div>
                </Card>
            </div>

            {/* Tables Row */}
            <div className="dashboard-tables">
                <Card className="table-card" title="Top 5 Most Punctual Teachers">
                    <DataTable value={topPunctual} size="small" stripedRows>
                        <Column field="tid" header="Teacher Sign ID" sortable style={{ width: '140px' }} />
                        <Column field="name" header="Name" />
                        <Column field="teacherType" header="Type" />
                        <Column field="punctuality" header="Punctuality" body={punctualityTemplate} style={{ width: '40%' }} />
                        <Column field="daysPresent" header="Days" style={{ width: '60px' }} />
                    </DataTable>
                </Card>

                <Card className="table-card" title="Teachers Needing Attention - (Last 30 Days Performance)">
                    <DataTable value={lowAttendance} size="small" stripedRows emptyMessage="All teachers have good attendance!">
                        <Column field="tid" header="Teacher Sign ID" sortable style={{ width: '140px' }} />
                        <Column field="name" header="Name" />
                        <Column field="teacherType" header="Type" />
                        <Column field="daysPresent" header="Days Present" style={{ width: '100px' }} />
                        <Column field="attendanceRate" header="Rate" body={attendanceRateTemplate} style={{ width: '80px' }} />
                    </DataTable>
                </Card>
            </div>

            {/* Bottom Row */}
            <div className="dashboard-bottom">
                <Card className="table-card" title="Machine Status">
                    <DataTable value={machineStatus} size="small" stripedRows>
                        <Column field="serialNumber" header="Serial Number" />
                        <Column field="location" header="Location" />
                        <Column field="lastSync" header="Last Sync" body={(row) => formatDateTime(row.lastSync)} />
                        <Column header="Status" body={machineStatusTemplate} style={{ width: '100px' }} />
                    </DataTable>
                </Card>

                <Card className="table-card" title="Recent Punch Activity (24h)">
                    <DataTable value={recentPunches} size="small" stripedRows emptyMessage="No recent punches">
                        <Column field="name" header="Teacher" />
                        <Column field="punchTime" header="Time" body={(row) => formatDateTime(row.punchTime)} />
                        <Column field="machine" header="Machine" />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
}

export default Dashboard;
